import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, MessageCircle, RefreshCw, ChefHat, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const MESSY_KITCHEN_IMAGE = "https://images.unsplash.com/photo-1596581144902-e8108142a141?w=800&q=80";

export default function ErrorPage({ error }) {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full text-center">
        {/* Animated 500 Number */}
        <motion.div
          initial={{ scale: 0, rotate: 10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mb-6"
        >
          <span className="font-['Playfair_Display'] text-[120px] sm:text-[180px] font-bold text-red-500/20 leading-none select-none">
            500
          </span>
        </motion.div>

        {/* Messy Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative mx-auto w-64 h-64 sm:w-80 sm:h-80 mb-8 -mt-20"
        >
          <div className="absolute inset-0 bg-red-500/10 rounded-full blur-3xl" />
          <img
            src={MESSY_KITCHEN_IMAGE}
            alt="Kitchen mess"
            className="relative w-full h-full object-cover rounded-2xl shadow-2xl border-4 border-white"
          />
          {/* Warning Decoration */}
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="absolute -top-4 -right-4 bg-red-500 rounded-full p-3 shadow-lg"
          >
            <AlertTriangle className="w-6 h-6 text-white" />
          </motion.div>
          {/* Chef Hat */}
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, -15, 15, -10, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            className="absolute -bottom-4 -left-4 bg-white rounded-full p-3 shadow-lg"
          >
            <ChefHat className="w-6 h-6 text-[#8D6E63]" />
          </motion.div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4"
        >
          A bit of a mess in the kitchen... 👨‍🍳
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-['Poppins'] text-lg text-stone-600 mb-8 max-w-md mx-auto"
        >
          Something went wrong on our server. Even the best chefs have tough days! 
          Our bakers are already cleaning up the mess.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            onClick={handleRefresh}
            size="lg"
            className="bg-[#8D6E63] hover:bg-[#8D6E63]/90 rounded-full px-8 font-['Poppins'] gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </Button>
          
          <Button
            asChild
            size="lg"
            className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full px-8 font-['Poppins'] gap-2"
          >
            <Link to="/">
              <Home className="w-5 h-5" />
              Back to Home
            </Link>
          </Button>
        </motion.div>

        {/* Contact Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6"
        >
          <Button
            asChild
            variant="ghost"
            className="font-['Poppins'] text-stone-500 hover:text-[#8D6E63]"
          >
            <Link to="/contact" className="gap-2">
              <MessageCircle className="w-4 h-4" />
              Need help? Contact us
            </Link>
          </Button>
        </motion.div>

        {/* Error Details (only in development) */}
        {error && process.env.NODE_ENV === 'development' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 p-4 bg-red-50 rounded-lg text-left max-w-lg mx-auto"
          >
            <p className="text-xs text-red-600 font-mono">
              {error.message || 'Unknown error'}
            </p>
          </motion.div>
        )}

        {/* Fun Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-12 text-xs text-stone-400 font-['Poppins']"
        >
          Error 500 • Internal server error • Mad Brothers Bakery
        </motion.p>
      </div>
    </div>
  );
}
